import styled from "@emotion/styled";
import { useConnection } from "@solana/wallet-adapter-react";
import { pendingRewardReducer } from "@stepfinance/farms";
import type { ConcentratedLiquidityPosition, LiquidityPoolPlatforms } from "@stepfinance/liquidity";
import type { StepColumnsType } from "@stepfinance/react";
import {
  CellDefault,
  CheckboxFilter,
  ContextButton,
  ExtraBoldText,
  formatBalance,
  getColumnSearchProps,
  makeSimpleFilters,
  PlatformIcon,
  StepAssetBreakdownPopoverChip,
  StepPopoverCellWrapper,
  StepTable,
} from "@stepfinance/react";
import type { TablePaginationConfig } from "antd/lib/table";
import type { FilterValue } from "antd/lib/table/interface";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";

import { claimWhirlpoolRewards } from "../../../actions/orca/claimWhirlpoolRewards";
import { useEnrichedWallet } from "../../../contexts/enrichedWallet";
import { useLanguageContext } from "../../../contexts/language/language";
import { useConnectedWallet } from "../../../hooks";
import { getMultipleRewardClaimsNotificationContent } from "../../../utils/notifications";
import { isPaginationHidden, useLocalStorageState } from "../../../utils/utils";
import { IconTextContainer, PAGE_SIZE_OPTIONS, StepPopoverAssetWithIconRow, TokenIconText } from "../../shared";
import CellValue from "../../Table/CellValue";
import { ChevronCollapse } from "../ChevronCollapse/ChevronCollapse";
import { PoolCell } from "../FarmGrid/PoolCell";
import { ConcentratedLiquidityBreakdown } from "./ConcentratedLiquidityBreakdown";

const rewardActionsByPlatform = {
  Orca: claimWhirlpoolRewards,
};

export const ConcentratedLiquidity: FC<{
  concentratedLiquidityPools: ConcentratedLiquidityPosition[];
  totalValue: number;
}> = ({ concentratedLiquidityPools, totalValue }) => {
  const { watching } = useEnrichedWallet();
  const wallet = useConnectedWallet();
  const { connection } = useConnection();
  const [filteredInfo, setFilteredInfo] = useState<Record<string, FilterValue | null>>({});
  const [pagination, setPagination] = useState<TablePaginationConfig>({});
  const [paginationSetting, setPaginationSetting] = useLocalStorageState(
    "concentratedLiquidityPaginationSizeSetting",
    PAGE_SIZE_OPTIONS.m[0],
  );

  const {
    currentLanguageData: {
      common,
      pages: {
        dashboard: {
          modules: {
            liquidity: { concentratedLiquidity: CLMMText },
          },
        },
      },
    },
  } = useLanguageContext();

  const claimRewardsByPlatform = useCallback(
    (record: ConcentratedLiquidityPosition) => () => {
      const rewardClaimAction = rewardActionsByPlatform[record.kind];
      const notificationContent = getMultipleRewardClaimsNotificationContent(record.rewards, common);
      rewardClaimAction({ connection, wallet, positionAddress: record.key, notificationContent });
    },
    [common, connection, wallet],
  );

  useEffect(() => {
    setPagination({
      hideOnSinglePage: isPaginationHidden(concentratedLiquidityPools.length, PAGE_SIZE_OPTIONS.m),
      showSizeChanger: true,
      pageSize: parseInt(paginationSetting),
      pageSizeOptions: PAGE_SIZE_OPTIONS.m,
    });
  }, [concentratedLiquidityPools.length, paginationSetting]);

  const columns: StepColumnsType<ConcentratedLiquidityPosition> = [
    {
      title: common.platform,
      dataIndex: "kind",
      key: "kind",
      filters: makeSimpleFilters(concentratedLiquidityPools.map((pool) => pool.kind)),
      filterDropdown: (props) => <CheckboxFilter {...props} />,
      onFilter: (value, record) => record.kind.startsWith(value as LiquidityPoolPlatforms),
      filteredValue: filteredInfo.kind ?? null,
      render(_, { kind }) {
        return (
          <IconTextContainer>
            <PlatformIcon names={[kind]} />
            <TokenIconText bold>{kind}</TokenIconText>
          </IconTextContainer>
        );
      },
      sorter: (a, b) => a.kind.localeCompare(b.kind),
    },
    {
      title: common.pool,
      dataIndex: "lpName",
      key: "lpName",
      ...getColumnSearchProps("lpName", "pool"),
      filteredValue: filteredInfo.lpName ?? null,
      render(_, record) {
        return (
          <PoolCell
            {...{
              ...record,
              analyticsLink: `https://analytics.step.finance/defionsolana/onChainMarkets?mint=${record.lpMintAddress}`,
            }}
          />
        );
      },
      sorter: ({ lpName: poolA }, { lpName: poolB }) => poolA.localeCompare(poolB),
    },
    {
      title: "APR",
      dataIndex: "apr",
      key: "apy",
      className: "column-number",
      render(_, record) {
        const noAPR = record.apr <= 0;
        const rateBreakdown = record.rateBreakdown ? record.rateBreakdown : [];
        const APR = record.apr;
        if (noAPR) {
          return <CellDefault />;
        } else {
          const breakdownContent = rateBreakdown.map((item, i) => {
            return (
              <ReturnBreakdown key={i}>
                <ExtraBoldText>{item.returnType}</ExtraBoldText>
                <CellValue cellType="percentage">{item.percentage}</CellValue>
              </ReturnBreakdown>
            );
          });

          return (
            <FlexedDiv>
              <CellValue cellType="percentage">{APR}</CellValue>
              {breakdownContent.length > 0 && (
                <div className="percentage-container">
                  <StepPopoverCellWrapper>
                    <StepAssetBreakdownPopoverChip
                      title={CLMMText.returnBreakdown.title}
                      content={breakdownContent}
                      onlyContent
                    />
                  </StepPopoverCellWrapper>
                </div>
              )}
            </FlexedDiv>
          );
        }
      },
      sorter: (a, b) => Number(a.apr) - Number(b.apr),
    },
    {
      title: common.pending,
      dataIndex: "pending",
      key: "pending",
      className: "column-number",
      render(_, record) {
        const pendingRewardUSD = record.rewards.reduce(pendingRewardReducer, 0);
        const rewardClaimAction = rewardActionsByPlatform[record.kind];
        const isClaimable = !watching && pendingRewardUSD > 0 && rewardClaimAction;
        const rewardRows = record.rewards
          .filter((r) => r.rewardBalance > 0)
          .map((r) => {
            const rewardBalanceDisplay: string = formatBalance(r.rewardBalance, r.rewardToken?.decimals);
            const symbol: string = r.rewardToken?.symbol;

            return (
              <StepPopoverAssetWithIconRow
                key={r.rewardToken.address}
                tokenIconMintAddress={r.rewardToken?.address}
                amount={rewardBalanceDisplay}
                symbol={symbol}
              />
            );
          }, []);

        return (
          <div>
            <StepPopoverCellWrapper>
              {isClaimable && (
                <ContextButtonStyled onClick={claimRewardsByPlatform(record)}>{common.claim}</ContextButtonStyled>
              )}
              <StepAssetBreakdownPopoverChip
                title={CLMMText.pendingBreakdown.title}
                content={rewardRows}
                assetValue={pendingRewardUSD}
                assetValueDisplay={<CellValue cellType="userPositionValue">{pendingRewardUSD}</CellValue>}
              />
            </StepPopoverCellWrapper>
          </div>
        );
      },
      sorter: (a, b) => a.rewards.reduce(pendingRewardReducer, 0) - b.rewards.reduce(pendingRewardReducer, 0),
    },
    {
      title: common.value,
      dataIndex: "value",
      key: "value",
      className: "column-number",
      render(_, record) {
        return <ConcentratedLiquidityBreakdown {...{ ...record, ...{ price: record.valueInUSD } }} />;
      },
      sorter: (a, b) => (a.valueInUSD ?? 0) - (b.valueInUSD ?? 0),
      defaultSortOrder: "descend",
    },
  ];
  return (
    <>
      {concentratedLiquidityPools.length > 0 && (
        <ConcentratedLiquidityContainer>
          <ChevronCollapse isCollapseChild title={CLMMText.title} value={totalValue}>
            <StepTable
              columns={columns}
              dataSource={concentratedLiquidityPools}
              pagination={pagination}
              onChange={(pagination, filters) => {
                setPaginationSetting(pagination.pageSize ? pagination.pageSize.toString() : PAGE_SIZE_OPTIONS.m[0]);
                setFilteredInfo(filters);
              }}
            />
          </ChevronCollapse>
        </ConcentratedLiquidityContainer>
      )}
    </>
  );
};

const FlexedDiv = styled.div`
  display: flex;
  justify-content: flex-end;

  .percentage-container {
    margin-left: 10px;
    display: flex;
    align-items: center;
  }
`;

const ConcentratedLiquidityContainer = styled.div`
  margin-top: 20px;
`;

const ReturnBreakdown = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ContextButtonStyled = styled(ContextButton)`
  margin-left: 0;
`;
